import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { DefaultWebViewOptions, InAppBrowser, ToolbarPosition, iOSAnimation, iOSViewStyle } from '@capacitor/inappbrowser';
import { CaddieCheckout } from '../plugins/caddieCheckout';
import { walletAPI, caddieAPI, BASE_WITHOUT_API } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Wallet, Send, RefreshCw, ArrowDownToLine, LogOut, Receipt, Users, UserCog, Check } from 'lucide-react';
import { toast } from 'sonner';
import SendMoneyDialog from '../components/SendMoneyDialog';
import RegisterCaddieDialog from '../components/RegisterCaddieDialog';
import ViewRegisteredCaddiesDialog from '../components/ViewRegisteredCaddiesDialog';
import PWAInstallBanner from '../components/PWAInstallBanner';
import NotificationBell from '../components/NotificationBell';
import { usePushNotifications } from '../hooks/usePushNotifications';
import {
  getAtlasWithdrawalDescription,
  getWithdrawalExpiry,
  getWithdrawalStatusPresentation,
  getWithdrawalSuccessMessage,
  getWithdrawalTransactionDetail,
  getWithdrawalTransactionTitle,
  canResendAtlasWithdrawalCode,
  isAtlasWithdrawal,
  isWithdrawalCodeExpired,
} from '../utils/withdrawalPresentation';
import {
  canResendAtlasCashPickupCode,
  getCashPickupReferenceId,
  getCashPickupTransactionDetail,
  getCashPickupTransactionStatusPresentation,
  getCashPickupTransactionTitle,
  isCashPickupTransaction,
} from '../utils/cashPickupPresentation';
import {
  clearAtlasCodeResendKey,
  getOrCreateAtlasCodeResendKey,
} from '../utils/atlasCodeResend';

const TOPUP_PROVIDERS = {
  payfast: 'PayFast',
  ozow: 'Ozow',
  stitch: 'Stitch',
};
const DEFAULT_TOPUP_PROVIDERS = [{ id: 'payfast', name: 'PayFast' }];
const ATLAS_CODE_RESEND_ENABLED = (
  process.env.REACT_APP_ATLAS_CODE_RESEND_ENABLED === 'true'
);

const createWithdrawalIdempotencyKey = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `withdrawal-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const MOBILE_CHECKOUT_OPTIONS = {
  ...DefaultWebViewOptions,
  showURL: false,
  showToolbar: true,
  clearCache: false,
  clearSessionCache: false,
  closeButtonText: 'Close',
  toolbarPosition: ToolbarPosition.TOP,
  showNavigationButtons: true,
  leftToRight: true,
  android: {
    ...DefaultWebViewOptions.android,
    allowZoom: false,
    hardwareBack: true,
    pauseMedia: true,
  },
  iOS: {
    ...DefaultWebViewOptions.iOS,
    viewStyle: iOSViewStyle.FULL_SCREEN,
    animationEffect: iOSAnimation.COVER_VERTICAL,
    allowsBackForwardNavigationGestures: true,
  },
};

const paymentReturnRouteFromUrl = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const path = parsed.protocol === 'app.caddiepay.mobile:'
      ? `/${parsed.host}${parsed.pathname}`
      : parsed.pathname;

    if (path !== '/payment/success' && path !== '/payment/cancel') {
      return null;
    }

    return `${path}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

function PaymentProviderMark({ provider }) {
  const providerId = provider.id?.toLowerCase();

  if (providerId === 'payfast') {
    return (
      <span className="payment-provider-logo payment-provider-logo-payfast" aria-hidden="true">
        <span className="payment-provider-logo-main">
          <span className="text-red-500">Pay</span><span className="text-slate-700 italic">Fast</span>
        </span>
        <span className="payment-provider-logo-sub">A DPO Company</span>
      </span>
    );
  }

  if (providerId === 'ozow') {
    return (
      <span className="payment-provider-logo payment-provider-logo-ozow" aria-hidden="true">
        <span className="payment-provider-logo-ozow-symbol" />
        <span className="payment-provider-logo-main font-extrabold tracking-wide text-slate-700">OZOW</span>
        <span className="payment-provider-logo-sub">Secure Payments</span>
      </span>
    );
  }

  return (
    <span className="payment-provider-logo payment-provider-logo-generic">
      {provider.name}
    </span>
  );
}

function PaymentMethodOption({ provider, checked, disabled, onChange }) {
  const label = TOPUP_PROVIDERS[provider.id] || provider.name;

  return (
    <label className={`payment-method-option ${checked ? 'is-selected' : ''} ${disabled ? 'is-disabled' : ''}`}>
      <input
        type="radio"
        name="reload-provider"
        value={provider.id}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="sr-only"
        aria-label={`${label} Sandbox`}
        data-testid={`reload-provider-${provider.id}`}
      />
      <span className="payment-method-radio" aria-hidden="true">
        {checked && <Check className="h-3.5 w-3.5" />}
      </span>
      <PaymentProviderMark provider={provider} />
    </label>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  usePushNotifications();
  const isNativeApp = Capacitor.isNativePlatform();
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadAmount, setReloadAmount] = useState('');
  const [reloadOtp, setReloadOtp] = useState('');
  const [reloadRequiresOtp, setReloadRequiresOtp] = useState(false);
  const [reloadProvider, setReloadProvider] = useState('payfast');
  const [availableTopupProviders, setAvailableTopupProviders] = useState(DEFAULT_TOPUP_PROVIDERS);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawalRefs, setWithdrawalRefs] = useState([]);
  const withdrawalRequestRef = useRef(null);
  const atlasResendRequestsRef = useRef({});
  const [atlasResendLoading, setAtlasResendLoading] = useState(null);
  const [showReloadDialog, setShowReloadDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showRegisterCaddieDialog, setShowRegisterCaddieDialog] = useState(false);
  const [showViewCaddiesDialog, setShowViewCaddiesDialog] = useState(false);
  const [selectedCaddieForPayment, setSelectedCaddieForPayment] = useState(null);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadTopupProviders = async () => {
      try {
        const response = await walletAPI.getTopupProviders();
        const enabled = (response.data.providers || []).filter((provider) => provider.enabled);
        setAvailableTopupProviders(enabled.length ? enabled : DEFAULT_TOPUP_PROVIDERS);
        if (!enabled.some((provider) => provider.id === reloadProvider)) {
          setReloadProvider('payfast');
        }
      } catch {
        setAvailableTopupProviders(DEFAULT_TOPUP_PROVIDERS);
        setReloadProvider('payfast');
      }
    };

    loadTopupProviders();
  }, [reloadProvider]);

  const loadDashboardData = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        navigate('/login');
        return;
      }
      
      const userData = JSON.parse(storedUser);
      setUser(userData);

      const [walletRes, transRes] = await Promise.all([
        walletAPI.getWallet(),
        walletAPI.getTransactions()
      ]);

      setWallet(walletRes.data);
      setTransactions(transRes.data.transactions);

      if (userData.user_type === 'caddie') {
        const refsRes = await caddieAPI.getWithdrawalReferences();
        setWithdrawalRefs(refsRes.data.references);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const [reloadLoading, setReloadLoading] = useState(false);
  const reloadProviderLabel = TOPUP_PROVIDERS[reloadProvider] || reloadProvider;

  const resetReloadFlow = () => {
    setReloadRequiresOtp(false);
    setReloadOtp('');
    setReloadAmount('');
  };

  const topupReturnUrls = () => {
    const appBaseUrl = isNativeApp ? BASE_WITHOUT_API : window.location.origin;
    const appReturn = isNativeApp ? '?app_return=1' : '';
    return {
      return_url: `${appBaseUrl}/payment/success${appReturn}`,
      cancel_url: `${appBaseUrl}/payment/cancel${appReturn}`,
    };
  };

  const selectedReturnUrls = () => (
    reloadProvider === 'payfast' && !isNativeApp ? {} : topupReturnUrls()
  );

  const initiateSelectedTopup = (data) => (
    reloadProvider === 'payfast'
      ? walletAPI.initiatePayfast(data)
      : walletAPI.initiateGatewayTopup(reloadProvider, data)
  );

  const handleNativeCheckoutReturn = useCallback(async (url) => {
    if (!isNativeApp) return false;

    const route = paymentReturnRouteFromUrl(url);
    if (!route) return false;

    await CaddieCheckout.close().catch(() => {});
    await InAppBrowser.close().catch(() => {});
    await Browser.close().catch(() => {});
    navigate(route, { replace: true });
    return true;
  }, [isNativeApp, navigate]);

  const openNativeCheckout = useCallback(async (checkoutUrl) => {
    let navigationHandle;
    let closedHandle;
    let handledReturn = false;

    const cleanupListeners = async () => {
      await navigationHandle?.remove?.();
      await closedHandle?.remove?.();
    };

    try {
      navigationHandle = await CaddieCheckout.addListener('checkoutPageNavigationCompleted', async ({ url }) => {
        if (!paymentReturnRouteFromUrl(url)) return;

        handledReturn = true;
        await cleanupListeners();
        await handleNativeCheckoutReturn(url);
      });

      closedHandle = await CaddieCheckout.addListener('checkoutClosed', async () => {
        await cleanupListeners();
        if (!handledReturn) {
          toast.info('Checkout closed. You can start the top-up again whenever you are ready.');
        }
      });

      await CaddieCheckout.open({
        url: checkoutUrl,
        title: 'CaddiePay',
        subtitle: 'Secure checkout',
      });
    } catch (error) {
      await cleanupListeners();
      console.warn('CaddiePay checkout WebView failed, falling back to plugin WebView:', error);

      navigationHandle = await InAppBrowser.addListener('browserPageNavigationCompleted', async ({ url }) => {
        if (!paymentReturnRouteFromUrl(url)) return;

        handledReturn = true;
        await cleanupListeners();
        await handleNativeCheckoutReturn(url);
      });

      closedHandle = await InAppBrowser.addListener('browserClosed', async () => {
        await cleanupListeners();
        if (!handledReturn) {
          toast.info('Checkout closed. You can start the top-up again whenever you are ready.');
        }
      });

      await InAppBrowser.openInWebView({
        url: checkoutUrl,
        options: MOBILE_CHECKOUT_OPTIONS,
      });
    }
  }, [handleNativeCheckoutReturn]);

  const requestReloadOtp = async () => {
    const otpResponse = await initiateSelectedTopup({
      amount: parseFloat(reloadAmount),
      payment_method: reloadProvider === 'payfast' ? 'cc' : 'instant_eft',
      ...selectedReturnUrls(),
    });
    setReloadRequiresOtp(true);
    toast.info('OTP sent to your phone. It is valid for 5 minutes.');
    return otpResponse;
  };

  const handleReload = async (e) => {
    e.preventDefault();
    setReloadLoading(true);
    try {
      if (!reloadRequiresOtp) {
        await requestReloadOtp();
        setReloadLoading(false);
        return;
      }

      const response = await initiateSelectedTopup({
        amount: parseFloat(reloadAmount),
        payment_method: reloadProvider === 'payfast' ? 'cc' : 'instant_eft',
        otp_code: reloadOtp.trim(),
        ...selectedReturnUrls(),
      });

      if (!response.data?.checkout_url) {
        throw new Error(`Missing ${reloadProviderLabel} checkout URL`);
      }

      if (response.data.checkout_method === 'POST') {
        const checkoutFields = response.data.checkout_fields || {};
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.data.checkout_url;
        Object.entries(checkoutFields).forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        setShowReloadDialog(false);
        setReloadLoading(false);
        form.submit();
        return;
      }

      if (isNativeApp) {
        // Native: present hosted checkout in a branded in-app WebView while keeping return handling in CaddiePay.
        setShowReloadDialog(false);
        setReloadLoading(false);
        await openNativeCheckout(response.data.checkout_url);
      } else {
        setShowReloadDialog(false);
        setReloadLoading(false);
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to initiate payment');
      setReloadLoading(false);
    }
  };

  const handleResendReloadOtp = async () => {
    if (!reloadAmount) {
      toast.error('Enter an amount first');
      return;
    }

    setReloadLoading(true);
    try {
      await requestReloadOtp();
      setReloadOtp('');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to resend OTP');
    } finally {
      setReloadLoading(false);
    }
  };

  const searchCaddies = async (query) => {
    if (query.length < 2) return [];
    try {
      const response = await caddieAPI.searchCaddies(query);
      return response.data.caddies;
    } catch (error) {
      console.error('Search failed:', error);
      return [];
    }
  };

  const handleGenerateWithdrawal = async (e) => {
    e.preventDefault();

    const amount = parseFloat(withdrawalAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid withdrawal amount');
      return;
    }
    if (amount > wallet?.balance) {
      toast.error(`Insufficient balance. Available: R${wallet?.balance?.toFixed(2)}`);
      return;
    }

    if (!withdrawalRequestRef.current || withdrawalRequestRef.current.amount !== amount) {
      withdrawalRequestRef.current = {
        amount,
        key: createWithdrawalIdempotencyKey(),
      };
    }

    setWithdrawalLoading(true);
    try {
      const response = await caddieAPI.generateWithdrawalReference(
        amount,
        withdrawalRequestRef.current.key
      );
      toast.success(getWithdrawalSuccessMessage(response.data));
      withdrawalRequestRef.current = null;
      setWithdrawalAmount('');
      setShowWithdrawDialog(false);
      await loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setWithdrawalLoading(false);
    }
  };

  const handleAtlasCodeResend = async (workflow, referenceId) => {
    const operationId = `${workflow}:${referenceId}`;
    const idempotencyKey = getOrCreateAtlasCodeResendKey(
      atlasResendRequestsRef.current,
      workflow,
      referenceId
    );
    setAtlasResendLoading(operationId);
    try {
      const response = workflow === 'withdrawal'
        ? await caddieAPI.resendWithdrawalAtlasCode(referenceId, idempotencyKey)
        : await walletAPI.resendCashPickupAtlasCode(referenceId, idempotencyKey);
      clearAtlasCodeResendKey(
        atlasResendRequestsRef.current,
        workflow,
        referenceId
      );
      toast.success(response.data?.message || 'Replacement Atlas code sent by SMS');
      await loadDashboardData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail
        || 'Could not resend the Atlas code. Please try again.'
      );
    } finally {
      setAtlasResendLoading(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleSelectCaddieForPayment = (caddie) => {
    setSelectedCaddieForPayment(caddie);
    setShowSendDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-emerald-900 mx-auto mb-4" />
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="app-safe-header bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="w-8 h-8 text-emerald-900" />
            <h1 className="text-2xl font-bold text-emerald-900">CaddiePay</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm text-slate-600">Welcome back,</p>
              <p className="font-semibold text-slate-900">{user?.full_name}</p>
            </div>
            <NotificationBell />
            <Button variant="ghost" onClick={() => navigate('/profile')} className="rounded-full" data-testid="profile-btn" title="Profile & Settings">
              <UserCog className="w-5 h-5" />
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="rounded-full" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PWAInstallBanner />
        {/* Balance Card */}
        <Card className="wallet-card mb-8 bg-gradient-to-br from-emerald-900 to-emerald-700 text-white border-0" data-testid="balance-card">
          <CardHeader>
            <CardDescription className="text-emerald-100">Available Balance</CardDescription>
            <CardTitle className="text-5xl font-bold" data-testid="balance-amount">
              R{wallet?.balance?.toFixed(2) || '0.00'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap">
              {user?.user_type === 'golfer' && (
                <>
	                  <Dialog
	                    open={showReloadDialog}
	                    onOpenChange={(open) => {
	                      setShowReloadDialog(open);
	                      if (!open) {
	                        resetReloadFlow();
	                      }
	                    }}
	                  >
                    <DialogTrigger asChild>
                      <Button className="btn-secondary" data-testid="reload-wallet-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Top-up Wallet
                      </Button>
                    </DialogTrigger>
	                    <DialogContent
                        className="bg-white text-slate-900 border border-slate-200 rounded-2xl shadow-2xl p-5 sm:p-6 gap-4 sm:max-w-lg"
                        data-testid="reload-dialog"
                      >
	                      <DialogHeader className="text-left">
	                        <DialogTitle>Top-up Your Wallet</DialogTitle>
	                        <DialogDescription className="text-slate-600">Add funds through a payment method</DialogDescription>
	                      </DialogHeader>
	                      <form onSubmit={handleReload} className="space-y-4">
	                        <fieldset className="space-y-2" disabled={reloadRequiresOtp}>
	                          <legend className="text-sm font-semibold text-slate-900">Methods of payment</legend>
                            <div className="payment-methods" data-testid="reload-provider-select">
	                            {availableTopupProviders.map((provider) => (
                                <PaymentMethodOption
                                  key={provider.id}
                                  provider={provider}
                                  checked={reloadProvider === provider.id}
                                  disabled={reloadRequiresOtp}
                                  onChange={(e) => setReloadProvider(e.target.value)}
                                />
	                            ))}
                            </div>
	                        </fieldset>
	                        <div className="space-y-2">
	                          <Label htmlFor="reload-amount">Amount (ZAR)</Label>
                          <Input
                            id="reload-amount"
                            type="number"
                            step="0.01"
                            min="10"
                            placeholder="500"
                            value={reloadAmount}
                            onChange={(e) => setReloadAmount(e.target.value)}
                            required
                            className="input-field"
                            data-testid="reload-amount-input"
                          />
                        </div>
	                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-800">
                            {reloadRequiresOtp
                              ? 'For security, enter the SMS OTP sent to your phone to continue. OTP expires in 5 minutes.'
                              : isNativeApp
                                ? `${reloadProviderLabel} checkout will open securely, and you will be brought back here after payment.`
	                                : `${reloadProviderLabel} checkout will open in this tab and return you here after payment.`}
                          </p>
                        </div>
                          {reloadRequiresOtp && (
                            <div className="space-y-2">
                              <Label htmlFor="reload-otp">SMS OTP</Label>
                              <Input
                                id="reload-otp"
                                type="text"
                                inputMode="numeric"
                                placeholder="Enter 6-digit OTP"
                                value={reloadOtp}
                                onChange={(e) => setReloadOtp(e.target.value)}
                                required
                                className="input-field"
	                                data-testid="reload-otp-input"
	                              />
                                <button
                                  type="button"
                                  onClick={handleResendReloadOtp}
                                  disabled={reloadLoading}
                                  className="text-xs text-emerald-800 underline underline-offset-2 disabled:opacity-50"
                                  data-testid="reload-resend-otp-btn"
                                >
                                  Resend OTP (invalidates previous code)
                                </button>
	                            </div>
	                          )}
	                        <Button type="submit" className="w-full btn-primary" disabled={reloadLoading} data-testid="reload-confirm-btn">
	                          {reloadLoading
	                              ? 'Processing...'
	                              : reloadRequiresOtp
	                                ? 'Verify OTP & Continue'
	                                : 'Send OTP'}
		                        </Button>
		                      </form>
		                    </DialogContent>
		                  </Dialog>

                  <Button className="btn-secondary" onClick={() => setShowSendDialog(true)} data-testid="send-money-btn">
                    <Send className="w-4 h-4 mr-2" />
                    Send Money
                  </Button>

                  <Button className="btn-secondary" onClick={() => setShowViewCaddiesDialog(true)} data-testid="my-caddies-btn">
                    <Users className="w-4 h-4 mr-2" />
                    My Caddies
                  </Button>
                </>
              )}

              {user?.user_type === 'caddie' && (
                <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-secondary" data-testid="generate-withdrawal-btn">
                      <ArrowDownToLine className="w-4 h-4 mr-2" />
                      Withdraw Funds
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="withdrawal-dialog">
                    <DialogHeader>
                      <DialogTitle>Withdraw Funds</DialogTitle>
                      <DialogDescription>
                        Enter an amount to create a cash withdrawal using the active withdrawal provider.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleGenerateWithdrawal} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="withdrawal-amount">Amount (ZAR)</Label>
                        <Input
                          id="withdrawal-amount"
                          type="number"
                          step="0.01"
                          min="1"
                          placeholder="500"
                          value={withdrawalAmount}
                          onChange={(e) => setWithdrawalAmount(e.target.value)}
                          required
                          className="input-field"
                          data-testid="withdrawal-amount-input"
                        />
                      </div>
                      <Button type="submit" className="w-full btn-primary" disabled={withdrawalLoading} data-testid="generate-confirm-btn">
                        {withdrawalLoading ? 'Processing...' : 'Withdraw Funds'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Withdrawals (Caddie Only) */}
        {user?.user_type === 'caddie' && withdrawalRefs.length > 0 && (
          <Card className="wallet-card mb-8" data-testid="withdrawal-refs-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Recent Withdrawals
              </CardTitle>
              <CardDescription className="text-slate-600">
                Track reference-based and Atlas voucher withdrawals
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {withdrawalRefs.slice(0, 3).map((ref) => {
                  const atlasWithdrawal = isAtlasWithdrawal(ref);
                  const expiry = getWithdrawalExpiry(ref);
                  const codeExpired = isWithdrawalCodeExpired(ref);
                  const canResendCode = ATLAS_CODE_RESEND_ENABLED
                    && canResendAtlasWithdrawalCode(ref);
                  const status = getWithdrawalStatusPresentation(ref.status, codeExpired);
                  const resendOperationId = `withdrawal:${ref.id}`;

                  return (
                    <div key={ref.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid={`withdrawal-ref-${ref.id}`}>
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {atlasWithdrawal ? 'Atlas voucher' : 'CaddiePay withdrawal reference'}
                          </p>
                          {atlasWithdrawal ? (
                            <p className="mt-1 text-xs text-slate-600">
                              {getAtlasWithdrawalDescription(ref.status, codeExpired)}
                            </p>
                          ) : (
                            <div className="reference-code mt-2" data-testid="reference-code">
                              {ref.reference_code}
                            </div>
                          )}
                        </div>
                        <Badge variant="outline" className={status.className}>
                          {status.label}
                        </Badge>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Amount:</span>
                        <span className="font-bold text-emerald-900">
                          R{Number(ref.amount).toFixed(2)}
                        </span>
                      </div>
                      {expiry && (
                        <div className="flex justify-between gap-4 text-sm mt-1">
                          <span className="text-slate-600">
                            {atlasWithdrawal ? 'Code expires:' : 'Reference expires:'}
                          </span>
                          <span className="text-right text-slate-900">
                            {atlasWithdrawal
                              ? new Date(expiry).toLocaleString()
                              : new Date(expiry).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {atlasWithdrawal && ref.status === 'redemption_code_delivered' && !codeExpired && (
                        <p className="mt-3 text-xs font-medium text-emerald-800">
                          Present the SMS code at a participating Flash merchant.
                        </p>
                      )}
                      {canResendCode && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="mt-3 border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                          disabled={atlasResendLoading !== null}
                          onClick={() => handleAtlasCodeResend('withdrawal', ref.id)}
                          data-testid={`resend-withdrawal-code-${ref.id}`}
                        >
                          <RefreshCw className={`mr-2 h-4 w-4 ${atlasResendLoading === resendOperationId ? 'animate-spin' : ''}`} />
                          {atlasResendLoading === resendOperationId
                            ? 'Sending replacement...'
                            : 'Resend code'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction History */}
        <Card className="wallet-card" data-testid="transactions-card">
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
            <CardDescription className="text-slate-600">Your latest wallet activity</CardDescription>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No transactions yet</p>
            ) : (
              <div className="space-y-0" data-testid="transaction-list">
                {transactions.map((txn) => {
                  const cashPickupTransaction = isCashPickupTransaction(txn);
                  const cashPickupStatus = cashPickupTransaction
                    ? getCashPickupTransactionStatusPresentation(txn)
                    : null;
                  const cashPickupReferenceId = getCashPickupReferenceId(txn);
                  const canResendCashPickupCode = ATLAS_CODE_RESEND_ENABLED
                    && user?.user_type === 'golfer'
                    && canResendAtlasCashPickupCode(txn);
                  const cashPickupResendOperationId = cashPickupReferenceId
                    ? `cash-pickup:${cashPickupReferenceId}`
                    : null;

                  return (
                    <div key={txn.id} className="transaction-item" data-testid={`transaction-${txn.id}`}>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {txn.transaction_type === 'wallet_reload' && (
                            `Wallet Top-up${txn.payment_provider_name ? ` via ${txn.payment_provider_name}` : ''}`
                          )}
                          {txn.transaction_type === 'send_money' && cashPickupTransaction && (
                            getCashPickupTransactionTitle(txn)
                          )}
                          {txn.transaction_type === 'send_money' && !cashPickupTransaction && (
                            txn.from_user_id === user?.id ? `Sent to ${txn.recipient_name}` : `Received from ${txn.sender_name}`
                          )}
                          {txn.transaction_type === 'withdrawal' && getWithdrawalTransactionTitle(txn)}
                          {txn.transaction_type === 'withdrawal_refund' && 'Withdrawal Refund'}
                          {txn.transaction_type === 'cash_pickup_refund' && getCashPickupTransactionTitle(txn)}
                        </p>
                        <p className="text-sm text-slate-600">
                          {txn.transaction_type === 'wallet_reload' && `Wallet ref: ${txn.reference_number}`}
                          {cashPickupTransaction && getCashPickupTransactionDetail(txn)}
                          {!cashPickupTransaction
                            && txn.transaction_type !== 'wallet_reload'
                            && txn.transaction_type !== 'withdrawal'
                            && txn.transaction_type !== 'cash_pickup_refund'
                            && txn.reference_number}
                          {txn.transaction_type === 'withdrawal' && getWithdrawalTransactionDetail(txn)}
                        </p>
                        {txn.transaction_type === 'wallet_reload' && txn.gateway_reference && (
                          <p className="text-xs text-slate-500">Gateway ref: {txn.gateway_reference}</p>
                        )}
                        {txn.transaction_type === 'wallet_reload' && txn.gateway_transaction_id && (
                          <p className="text-xs text-slate-500">Provider transaction: {txn.gateway_transaction_id}</p>
                        )}
                        {txn.transaction_type === 'wallet_reload' && txn.gateway_request_id
                          && txn.gateway_request_id !== txn.gateway_transaction_id && (
                          <p className="text-xs text-slate-500">Provider request: {txn.gateway_request_id}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">{new Date(txn.created_at).toLocaleString()}</p>
                        {canResendCashPickupCode && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="mt-3 border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                            disabled={atlasResendLoading !== null}
                            onClick={() => handleAtlasCodeResend(
                              'cash-pickup',
                              cashPickupReferenceId
                            )}
                            data-testid={`resend-cash-pickup-code-${cashPickupReferenceId}`}
                          >
                            <RefreshCw className={`mr-2 h-4 w-4 ${atlasResendLoading === cashPickupResendOperationId ? 'animate-spin' : ''}`} />
                            {atlasResendLoading === cashPickupResendOperationId
                              ? 'Sending replacement...'
                              : 'Resend code'}
                          </Button>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          txn.transaction_type === 'withdrawal' || (txn.transaction_type === 'send_money' && txn.from_user_id === user?.id)
                            ? 'text-red-600'
                            : 'text-emerald-600'
                        }`}>
                          {txn.transaction_type === 'withdrawal' || (txn.transaction_type === 'send_money' && txn.from_user_id === user?.id) ? '-' : '+'}
                          R{txn.amount.toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-500 capitalize">
                          {cashPickupStatus?.label || txn.status}
                        </p>
                        {txn.balance_after != null && (
                          <p className="text-xs text-slate-400 mt-0.5" data-testid={`balance-after-${txn.id}`}>Bal: R{txn.balance_after.toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Send Money Dialog */}
      {user?.user_type === 'golfer' && (
        <>
          <SendMoneyDialog
            open={showSendDialog}
            onOpenChange={(open) => {
              setShowSendDialog(open);
              if (!open) setSelectedCaddieForPayment(null); // Clear selection when closing
            }}
            onSuccess={loadDashboardData}
            searchCaddies={searchCaddies}
            prefilledCaddie={selectedCaddieForPayment}
          />

          <RegisterCaddieDialog
            open={showRegisterCaddieDialog}
            onOpenChange={setShowRegisterCaddieDialog}
            onSuccess={loadDashboardData}
          />

          <ViewRegisteredCaddiesDialog
            open={showViewCaddiesDialog}
            onOpenChange={setShowViewCaddiesDialog}
            onSelectCaddie={handleSelectCaddieForPayment}
            onRegisterNew={() => setShowRegisterCaddieDialog(true)}
          />
        </>
      )}
    </div>
  );
}

export default Dashboard;
